import {
  useGetProductsQuery,
  useGetStorageLocationsQuery,
  useGetStorefrontConfigAllocations,
  useSaveStorefrontConfigs,
} from "@fero/hooks";
import { AppRoutes } from "@fero/utils";
import { Alert } from "@mui/material";
import type { WithDbId } from "adl-gen/common/db";
import type {
  NominatedPurchaserDetailsView,
  NomPurchaserStorefrontConfigData,
  SaveStorefrontConfigsReq,
  StorageLocationView,
  StorefrontProductAllocationData,
} from "adl-gen/ferovinum/app/api";
import type { Product } from "adl-gen/ferovinum/app/db";
import { Loading } from "components/widgets/loader/loader";
import { useCallback, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  type AllocationWithProducts,
  NewPurchaserAllocationPageView,
} from "./new-purchaser-allocation-page-view";

export const NewPurchaserAllocationPage = () => {
  const location = useLocation();
  const history = useHistory();
  const { selectedStorefrontConfigs, purchaserDetails } = location.state as {
    selectedStorefrontConfigs: NomPurchaserStorefrontConfigData[];
    purchaserDetails: NominatedPurchaserDetailsView[];
  };
  const [activeStorageLocation, setActiveStorageLocation] =
    useState<StorageLocationView>();
  const [saveConfigError, setSaveConfigError] = useState<string | null>(null);
  const configAllocations = useGetStorefrontConfigAllocations(
    selectedStorefrontConfigs[0].nominatedPurchaserId,
  );
  const storageLocations = useGetStorageLocationsQuery({
    onSuccess: (data: StorageLocationView[]) =>
      getLoadedConfigStorageLocation(data),
  });
  const saveStorefrontConfigs = useSaveStorefrontConfigs();
  const products = useGetProductsQuery({
    productIds:
      configAllocations.data?.map(
        (a: StorefrontProductAllocationData) => a.productId,
      ) ?? [],
    enabled: configAllocations.isSuccess,
  });

  const getLoadedConfigStorageLocation = useCallback(
    (loadedLocations: StorageLocationView[]) => {
      setActiveStorageLocation(
        loadedLocations.find(
          (l) => l.id === selectedStorefrontConfigs[0].config.storageLocationId,
        ),
      );
    },
    [selectedStorefrontConfigs],
  );

  const onSaveConfig = useCallback(
    (req: SaveStorefrontConfigsReq) => {
      saveStorefrontConfigs.mutate(
        {
          ...req,
          config: {
            ...req.config,
            storageLocationId: activeStorageLocation
              ? activeStorageLocation.id
              : req.config.storageLocationId,
          },
        },
        {
          onSuccess: () => {
            history.push(AppRoutes.Storefront);
          },
          onError: (error: unknown) => {
            const errorMsg =
              (error as Error).message || "An unknown error occurred.";
            setSaveConfigError(errorMsg);
          },
        },
      );
    },
    [saveStorefrontConfigs, history, activeStorageLocation],
  );

  const onChangeActiveStorageLocation = useCallback(
    (newLocation: StorageLocationView | null) => {
      if (newLocation) setActiveStorageLocation(newLocation);
    },
    [],
  );

  const allocationsWithProducts: AllocationWithProducts[] = useMemo(() => {
    if (selectedStorefrontConfigs.length > 1) return [];
    return (
      configAllocations.data?.map(
        (allocData: StorefrontProductAllocationData) => {
          const product = products.data?.find(
            (p: WithDbId<Product>) => p.id === allocData.productId,
          );
          return { allocationData: allocData, product };
        },
      ) ?? []
    );
  }, [configAllocations.data, products.data, selectedStorefrontConfigs]);

  const isLoading =
    configAllocations.isLoading ||
    storageLocations.isLoading ||
    products.isLoading;
  if (isLoading) return <Loading loading={isLoading} fullScreen={true} />;

  const isError =
    configAllocations.isError ||
    storageLocations.isError ||
    products.isError ||
    activeStorageLocation === undefined;
  if (isError)
    return (
      <Alert severity="error">
        An error occurred:
        {configAllocations.isError && configAllocations.error}
        {configAllocations.isError && configAllocations.error}
        {configAllocations.isError && configAllocations.error}
        {activeStorageLocation === undefined && "Storage location not found."}
      </Alert>
    );

  return (
    <NewPurchaserAllocationPageView
      configs={selectedStorefrontConfigs}
      configStorageLocation={activeStorageLocation}
      orgStorageLocations={storageLocations.data}
      onChangeStorageLocation={onChangeActiveStorageLocation}
      allocationsWithProducts={allocationsWithProducts}
      onSaveConfig={onSaveConfig}
      errorOnSaveConfig={saveConfigError}
      nominatedPurchasersDetailsViews={purchaserDetails}
    />
  );
};
